/**
 * UX3 — Conversation-first agent run workspace (`/ai-studio/runs/:runId`).
 *
 * Persona chrome, conversation (choices in chat), Outputs rail (artifact popups),
 * past-runs sidebar. Decision Case / JSON compute stay behind Advanced (Design §2.2).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import {
  ConversationPicker,
  OutputsPanel,
  buildRunArtifacts,
  useConversation,
} from '@/components/ai-studio/conversation';
import {
  RunComputePanel,
  RunExecutionTracePanel,
  RunHistorySidebar,
  RunConversationsSidebar,
  RunWorkspaceChatPane,
  DecisionModeBanner,
  buildPersonaWelcome,
  summarizeWorkingSet,
  readDecisionModeFromWorkingSet,
} from '@/components/ai-studio/runs';
import {
  ASSIST_WORKSPACE_CONTAINER_CLASS,
  ASSIST_WORKSPACE_PAGE_CLASS,
} from '@/components/ai-studio/assist/assistPaneStyles';
import { InstanceSettingsDrawer } from '@/components/ai-studio/instances';
import {
  createConversation,
  getConversation,
  listConversationMessages,
  listConversations,
  renameConversation,
} from '@/services/aiStudioService';
import {
  createRun,
  getRun,
  getRunReadiness,
  listRunArtifacts,
  listRuns,
  startNewEpisode,
} from '@/services/aiStudioAgentService';
import api from '@/services/api';
import { toActiveContext } from '@/types/aiStudio';
import type {
  ActiveConversationContext,
  AiStudioConversation,
  AiStudioConversationSummary,
  ConversationMessage,
} from '@/types/aiStudio';
import type { PersistedRunArtifactDto } from '@/components/ai-studio/conversation';
import type { AiStudioRun } from '@/types/aiStudioAgent';

const AIStudioRunWorkspacePage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { runId = '' } = useParams<{ runId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const conversationIdParam = searchParams.get('conversationId');
  const instanceIdParam = searchParams.get('instanceId');
  const welcomeInjectedRef = useRef<string | null>(null);
  const autoOpenedConversationRef = useRef(false);

  const [settingsOpen, setSettingsOpen] = useState(false);

  const [run, setRun] = useState<AiStudioRun | null>(null);
  const [runLoading, setRunLoading] = useState(true);
  const [runErrorKey, setRunErrorKey] = useState<string | null>(null);

  const [conversation, setConversation] = useState<AiStudioConversation | null>(null);
  const [conversationSummaries, setConversationSummaries] = useState<AiStudioConversationSummary[]>(
    []
  );
  const [loadingConversationsList, setLoadingConversationsList] = useState(false);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [creatingConversation, setCreatingConversation] = useState(false);
  const [pageErrorKey, setPageErrorKey] = useState<string | null>(null);

  const [agentRuns, setAgentRuns] = useState<AiStudioRun[]>([]);
  /** True until past-runs fetch completes (or we know there is no agent to filter). */
  const [historyLoading, setHistoryLoading] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [readinessMessages, setReadinessMessages] = useState<string[]>([]);
  const [agentArchived, setAgentArchived] = useState(false);
  const [startingNewRun, setStartingNewRun] = useState(false);
  const [persistedArtifacts, setPersistedArtifacts] = useState<PersistedRunArtifactDto[]>([]);
  const [selectedModelId, setSelectedModelId] = useState('');
  const [modelOptions, setModelOptions] = useState<
    Array<{ id: string; label: string; disabled?: boolean }>
  >([]);

  const {
    messages,
    sending,
    error: sendError,
    setMessages,
    send,
  } = useConversation({
    conversationId: conversation?.id ?? null,
    modelConfigurationId: selectedModelId || null,
    sendErrorMessage: t('aiStudio.conversation.sendFailed'),
    onTurnComplete: () => {
      if (!runId) return;
      void Promise.all([getRun(runId), listRunArtifacts(runId)])
        .then(([nextRun, artifacts]) => {
          setRun(nextRun);
          setPersistedArtifacts(artifacts);
        })
        .catch(() => {
          /* non-blocking refresh after tool turns */
        });
      void listConversations({ contextType: 'AGENT_RUN', runId })
        .then(setConversationSummaries)
        .catch(() => {
          /* non-blocking */
        });
    },
  });

  /** Load LLM models (same source as planning chat). */
  useEffect(() => {
    let cancelled = false;
    void api
      .get<{ models: Array<{ id: string; displayName?: string; modelName?: string; isActive?: boolean }> }>(
        'ai/models/merged'
      )
      .then((res) => {
        if (cancelled) return;
        const models = (res.data.models ?? [])
          .filter((m) => m.isActive !== false)
          .map((m) => ({
            id: m.id,
            label: m.displayName || m.modelName || m.id,
          }));
        setModelOptions(models);
        if (!selectedModelId && models[0]) setSelectedModelId(models[0].id);
      })
      .catch(() => {
        /* model picker optional */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once on mount
  }, []);

  /** Load run + readiness. Artifacts are optional — never block run workspace on artifact API failure. */
  useEffect(() => {
    if (!runId) return;
    welcomeInjectedRef.current = null;
    let cancelled = false;
    setRunLoading(true);
    setRunErrorKey(null);
    void getRun(runId)
      .then((loaded) => {
        if (!cancelled) setRun(loaded);
      })
      .catch(() => {
        if (!cancelled) setRunErrorKey('aiStudio.workspace.runLoadFailed');
      })
      .finally(() => {
        if (!cancelled) setRunLoading(false);
      });
    void listRunArtifacts(runId)
      .then((artifacts) => {
        if (!cancelled) setPersistedArtifacts(artifacts);
      })
      .catch(() => {
        /* artifacts rail optional until migration / tool turns produce rows */
      });
    getRunReadiness(runId)
      .then((r) => {
        if (cancelled) return;
        setReadinessMessages(r.readiness.ready ? [] : r.readiness.messages);
        setAgentArchived(r.agentArchived);
      })
      .catch(() => {
        /* non-blocking */
      });
    return () => {
      cancelled = true;
    };
  }, [runId]);

  /** Past runs for this agent — hidden when opened from an instance (history lives in settings). */
  useEffect(() => {
    if (instanceIdParam) {
      setHistoryLoading(false);
      setAgentRuns([]);
      return;
    }
    if (!run?.agentId && !run?.agentCode) {
      if (run) setHistoryLoading(false);
      return;
    }
    let cancelled = false;
    setHistoryLoading(true);
    listRuns()
      .then((all) => {
        if (cancelled) return;
        const filtered = all.filter((r) =>
          run.agentId
            ? r.agentId === run.agentId
            : r.agentCode === run.agentCode
        );
        setAgentRuns(filtered);
      })
      .catch(() => {
        if (!cancelled) setAgentRuns([]);
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [instanceIdParam, run, run?.agentId, run?.agentCode]);

  /** Conversations for this run — user picks one to continue or starts new. */
  useEffect(() => {
    if (!runId) return;
    let cancelled = false;
    setLoadingConversationsList(true);
    listConversations({ contextType: 'AGENT_RUN', runId })
      .then((items) => {
        if (!cancelled) setConversationSummaries(items);
      })
      .catch(() => {
        if (!cancelled) setConversationSummaries([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingConversationsList(false);
      });
    return () => {
      cancelled = true;
    };
  }, [runId]);

  const openConversation = useCallback(
    (id: string) => {
      welcomeInjectedRef.current = null;
      const next = new URLSearchParams(searchParams);
      next.set('conversationId', id);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleEnsureConversationOnRun = useCallback(async () => {
    if (!run) return;
    setCreatingConversation(true);
    setPageErrorKey(null);
    try {
      const created = await createConversation({
        contextType: 'AGENT_RUN',
        runId: run.id,
        bindingId: run.bindingId,
        contextRef: { label: run.agentName ?? run.runName },
      });
      setConversationSummaries((current) => [
        {
          id: created.id,
          name: created.name,
          contextType: created.contextType,
          status: created.status,
          runId: created.runId,
          bindingId: created.bindingId,
          shellMetadata: created.shellMetadata,
          createdAt: created.createdAt,
          updatedAt: created.updatedAt,
        },
        ...current,
      ]);
      openConversation(created.id);
    } catch {
      setPageErrorKey('aiStudio.conversation.createFailed');
    } finally {
      setCreatingConversation(false);
    }
  }, [openConversation, run]);

  /** New conversation = new run (episode) on the same instance. */
  const handleNewConversation = useCallback(async () => {
    if (!run?.bindingId) return;
    setCreatingConversation(true);
    setPageErrorKey(null);
    try {
      const { run: nextRun, conversationId } = await startNewEpisode({
        instanceId: run.bindingId,
        runName: `${run.agentName ?? run.runName} — ${new Date()
          .toISOString()
          .slice(0, 16)
          .replace('T', ' ')}`,
      });
      const qs = new URLSearchParams();
      qs.set('conversationId', conversationId);
      if (instanceIdParam) qs.set('instanceId', instanceIdParam);
      else qs.set('instanceId', run.bindingId);
      navigate(`/ai-studio/runs/${nextRun.id}?${qs.toString()}`, { replace: false });
    } catch {
      setPageErrorKey('aiStudio.conversation.createFailed');
    } finally {
      setCreatingConversation(false);
    }
  }, [instanceIdParam, navigate, run]);

  /** Open the latest conversation (or create one on this run) — skip the picker by default. */
  useEffect(() => {
    if (!runId || conversationIdParam || loadingConversationsList || creatingConversation) return;
    if (autoOpenedConversationRef.current) return;

    if (conversationSummaries.length > 0) {
      autoOpenedConversationRef.current = true;
      const sorted = [...conversationSummaries].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
      openConversation(sorted[0].id);
      return;
    }

    autoOpenedConversationRef.current = true;
    void handleEnsureConversationOnRun();
  }, [
    conversationIdParam,
    conversationSummaries,
    creatingConversation,
    handleEnsureConversationOnRun,
    loadingConversationsList,
    openConversation,
    runId,
  ]);

  useEffect(() => {
    autoOpenedConversationRef.current = false;
  }, [runId]);

  const handleRenameConversation = useCallback(
    async (conversationId: string, title: string) => {
      const updated = await renameConversation(conversationId, title);
      setConversationSummaries((current) =>
        current.map((row) =>
          row.id === conversationId
            ? {
                ...row,
                name: updated.name,
                shellMetadata: updated.shellMetadata,
                updatedAt: updated.updatedAt,
              }
            : row
        )
      );
      if (conversation?.id === conversationId) {
        setConversation(updated);
      }
    },
    [conversation?.id]
  );

  useEffect(() => {
    if (!conversationIdParam) {
      setConversation(null);
      setMessages([]);
      return;
    }
    let cancelled = false;
    setLoadingConversation(true);
    setPageErrorKey(null);
    Promise.all([
      getConversation(conversationIdParam),
      listConversationMessages(conversationIdParam),
    ])
      .then(([loadedConversation, history]) => {
        if (cancelled) return;
        if (loadedConversation.runId && loadedConversation.runId !== runId) {
          setPageErrorKey('aiStudio.workspace.conversationMismatch');
          setConversation(null);
          setMessages([]);
          return;
        }
        setConversation(loadedConversation);
        if (history.length > 0) {
          setMessages(history);
          welcomeInjectedRef.current = conversationIdParam;
        } else {
          setMessages([]);
        }
      })
      .catch(() => {
        if (!cancelled) setPageErrorKey('aiStudio.conversation.loadFailed');
      })
      .finally(() => {
        if (!cancelled) setLoadingConversation(false);
      });
    return () => {
      cancelled = true;
    };
  }, [conversationIdParam, runId, setMessages]);

  /**
   * Inject persona welcome once when the thread is empty.
   * Prefer this run’s working set; else the latest past run for the same agent
   * (UX3 “latest configuration I have”).
   */
  useEffect(() => {
    if (!conversationIdParam || !run || loadingConversation || historyLoading) return;
    if (welcomeInjectedRef.current === conversationIdParam) return;
    if (messages.length > 0) {
      welcomeInjectedRef.current = conversationIdParam;
      return;
    }

    const fromThisRun = summarizeWorkingSet(run.workingSet);
    let priorSummary = fromThisRun;
    if (!priorSummary) {
      for (const past of agentRuns) {
        if (past.id === run.id) continue;
        const summary = summarizeWorkingSet(past.workingSet);
        if (summary) {
          priorSummary = summary;
          break;
        }
      }
    }
    const welcome = buildPersonaWelcome({
      agentName: run.agentName ?? t('aiStudio.workspace.defaultAgentName'),
      persona: run.agentExpertPosture ?? null,
      purpose: run.agentPurpose ?? null,
      hasPriorConfig: Boolean(priorSummary),
      priorSummary,
    });
    const bubble: ConversationMessage = {
      id: `welcome-${conversationIdParam}`,
      role: 'assistant',
      content: welcome,
      createdAt: new Date().toISOString(),
    };
    setMessages([bubble]);
    welcomeInjectedRef.current = conversationIdParam;
  }, [
    agentRuns,
    conversationIdParam,
    historyLoading,
    loadingConversation,
    messages.length,
    run,
    setMessages,
    t,
  ]);

  const openRun = useCallback(
    (id: string) => {
      const params = instanceIdParam ? `?instanceId=${encodeURIComponent(instanceIdParam)}` : '';
      navigate(`/ai-studio/runs/${encodeURIComponent(id)}${params}`);
    },
    [instanceIdParam, navigate]
  );

  const handleNewRun = useCallback(async () => {
    if (!run?.bindingId) {
      setPageErrorKey('aiStudio.runs.form.noRunnableInstances');
      return;
    }
    setStartingNewRun(true);
    setPageErrorKey(null);
    try {
      const newRun = await createRun({
        instanceId: run.bindingId,
        runName: `${run.agentName ?? 'Agent'} · ${new Date().toISOString().slice(0, 10)}`,
      });
      const params = new URLSearchParams();
      if (instanceIdParam) params.set('instanceId', instanceIdParam);
      const qs = params.toString();
      navigate(`/ai-studio/runs/${encodeURIComponent(newRun.id)}${qs ? `?${qs}` : ''}`);
    } catch {
      setPageErrorKey('aiStudio.agents.runFailed');
    } finally {
      setStartingNewRun(false);
    }
  }, [instanceIdParam, navigate, run]);

  const agentName = run?.agentName ?? t('aiStudio.workspace.defaultAgentName');
  const activeContext: ActiveConversationContext = conversation
    ? toActiveContext(conversation)
    : {
        contextType: 'AGENT_RUN',
        scopeLabel: agentName,
      };

  const outputArtifacts = useMemo(
    () => (run ? buildRunArtifacts(run, persistedArtifacts) : []),
    [run, persistedArtifacts]
  );

  const decisionMode = useMemo(
    () => (run ? readDecisionModeFromWorkingSet(run.workingSet) : null),
    [run]
  );
  const decisionModeBanner = useMemo(
    () => (decisionMode ? <DecisionModeBanner mode={decisionMode} /> : null),
    [decisionMode]
  );

  const historyPanel = useMemo(
    () =>
      run ? (
        <div className="flex flex-col gap-3">
          <RunConversationsSidebar
            currentConversationId={conversationIdParam}
            conversations={conversationSummaries}
            loading={loadingConversationsList}
            creating={creatingConversation}
            onSelect={openConversation}
            onNew={() => void handleNewConversation()}
            onRename={handleRenameConversation}
          />
          {!instanceIdParam && (
            <RunHistorySidebar
              currentRunId={run.id}
              runs={agentRuns}
              loading={historyLoading}
              onSelectRun={openRun}
              onNewRun={() => void handleNewRun()}
            />
          )}
        </div>
      ) : null,
    [
      agentRuns,
      conversationIdParam,
      conversationSummaries,
      creatingConversation,
      handleNewConversation,
      handleNewRun,
      handleRenameConversation,
      historyLoading,
      instanceIdParam,
      loadingConversationsList,
      openConversation,
      openRun,
      run,
    ]
  );

  const outputsPanel = useMemo(
    () => <OutputsPanel artifacts={outputArtifacts} />,
    [outputArtifacts]
  );

  if (runLoading) {
    return (
      <main className="px-4 py-4 md:px-6 lg:px-8">
        <p className="text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.workspace.loading')}
        </p>
      </main>
    );
  }

  if (runErrorKey || !run) {
    return (
      <main className="flex flex-col gap-3 px-4 py-4 md:px-6 lg:px-8">
        <p role="alert" className="text-base text-[var(--color-error)]">
          {t(runErrorKey ?? 'aiStudio.workspace.runLoadFailed')}
        </p>
      </main>
    );
  }

  return (
    <main className={`${ASSIST_WORKSPACE_PAGE_CLASS} w-full px-2 md:px-4`}>
      <div className={`${ASSIST_WORKSPACE_CONTAINER_CLASS} max-w-none`}>
      {pageErrorKey && (
        <div
          role="alert"
          className="flex-shrink-0 rounded border border-[var(--color-error)] bg-[var(--color-surface-bg)] px-4 py-2 text-base text-[var(--color-error)]"
        >
          {t(pageErrorKey)}
        </div>
      )}

      {agentArchived && (
        <div
          role="status"
          className="flex-shrink-0 rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-4 py-2 text-base text-[var(--color-surface-text)]"
        >
          {t('aiStudio.workspace.archivedWarning')}
        </div>
      )}

      {run?.isTrial && run.scopeContext?.['studio_test'] === true && (
        <div
          role="status"
          className="flex-shrink-0 rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-4 py-2 text-base text-[var(--color-surface-text)]"
        >
          {t('aiStudio.workspace.factoryTestBanner')}
        </div>
      )}

      {decisionModeBanner && !conversationIdParam ? (
        <div className="flex-shrink-0">{decisionModeBanner}</div>
      ) : null}

      {readinessMessages.length > 0 && (
        <div
          role="status"
          className="flex-shrink-0 rounded border border-[var(--color-error)] bg-[var(--color-surface-bg)] px-4 py-2 text-base text-[var(--color-error)]"
        >
          <p className="font-medium">{t('aiStudio.workspace.readinessTitle')}</p>
          <ul className="mt-1 list-disc pl-5">
            {readinessMessages.map((msg) => (
              <li key={msg}>{msg}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="lg:hidden flex-shrink-0 flex flex-col gap-3">
        {outputsPanel}
        {historyPanel}
      </div>

      {creatingConversation || (loadingConversation && conversationIdParam && !conversation) || startingNewRun || (!conversationIdParam && loadingConversationsList) ? (
        <p className="text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.conversation.loading')}
        </p>
      ) : !conversationIdParam ? (
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)] p-4">
          <p className="text-base text-[var(--color-surface-text-secondary)]">
            {t('aiStudio.workspace.selectConversationPrompt')}
          </p>
          <ConversationPicker
            conversations={conversationSummaries}
            loading={loadingConversationsList}
            creating={creatingConversation}
            onCreate={() => void handleNewConversation()}
            onSelect={openConversation}
          />
        </div>
      ) : (
        <RunWorkspaceChatPane
          className="min-h-0 flex-1"
          agentName={agentName}
          persona={run.agentExpertPosture ?? null}
          purpose={run.agentPurpose ?? null}
          headerActions={
            instanceIdParam ? (
              <button
                type="button"
                onClick={() => setSettingsOpen(true)}
                className="flex shrink-0 items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-3 py-1.5 text-sm text-[var(--color-surface-text)] hover:bg-[var(--color-surface-hover)]"
              >
                <Cog6ToothIcon className="h-5 w-5" aria-hidden />
                <span className="hidden sm:inline">{t('aiStudio.workspace.instanceSettings')}</span>
              </button>
            ) : undefined
          }
          context={activeContext}
          messages={messages}
          sending={sending}
          error={sendError}
          onSend={(text) => void send(text)}
          disabled={!conversation || readinessMessages.length > 0}
          loading={loadingConversation && Boolean(conversationIdParam) && !conversation}
          composerPlaceholder={t('aiStudio.workspace.composerHint')}
          emptyStateText={t('aiStudio.workspace.chatEmpty')}
          sidePanel={historyPanel}
          outputsPanel={outputsPanel}
          models={modelOptions}
          selectedModelId={selectedModelId}
          onModelChange={setSelectedModelId}
          statusBanner={decisionModeBanner}
        />
      )}

      <div className="flex-shrink-0 border-t border-[var(--color-border)] pt-2">
        <button
          type="button"
          className="text-base text-[var(--color-info)] hover:underline"
          onClick={() => setShowAdvanced((v) => !v)}
        >
          {showAdvanced
            ? t('aiStudio.workspace.hideAdvanced')
            : t('aiStudio.workspace.showAdvanced')}
        </button>
        {showAdvanced && (
          <div className="mt-2 max-h-64 overflow-y-auto">
            <RunExecutionTracePanel runId={run.id} />
            <RunComputePanel
              run={run}
              onRunUpdated={setRun}
              onDecisionCaseOpened={() => {
                /* Decision Case not primary UX3 */
              }}
            />
          </div>
        )}
      </div>

      {instanceIdParam && (
        <InstanceSettingsDrawer
          instanceId={instanceIdParam}
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
        />
      )}
      </div>
    </main>
  );
};

export default AIStudioRunWorkspacePage;
